import React, { createContext, useContext, useEffect, useMemo, useState, FC, ReactNode } from 'react';
import type { SxProps, Theme } from '@mui/material/styles';

export interface MobileHeaderTab {
  label: ReactNode;
  value: string | number;
}

export interface MobileHeaderTabsConfig {
  type: 'tabs';
  tabs: MobileHeaderTab[];
  value: string | number;
  onChange: (value: string | number) => void;
  /** 'fullWidth' (default) spreads tabs; 'scrollable' left-aligns and scrolls. */
  variant?: 'fullWidth' | 'scrollable';
  /** Extra styles merged onto the Tabs. */
  sx?: SxProps<Theme>;
}

export interface MobileHeaderHeadingConfig {
  type: 'heading';
  title: ReactNode;
  /** Optional right-aligned content (a chip, button, etc.). */
  action?: ReactNode;
  /** Extra styles merged onto the heading container. */
  sx?: SxProps<Theme>;
}

export type MobileHeaderConfig = MobileHeaderTabsConfig | MobileHeaderHeadingConfig | null;

/**
 * A page's primary action, handed to the bottom bar.
 *
 * When set, the bottom bar stops being navigation and becomes this one button —
 * the compose flow's Post/Update action. The page owns the behaviour and the
 * label; the bar owns the geometry and the animation.
 */
export interface MobileComposeAction {
  label: string;
  /** Shown while `busy` — e.g. "Posting". */
  busyLabel: string;
  busy: boolean;
  disabled: boolean;
  onSubmit: () => void;
}

interface MobileHeaderContextValue {
  /** True only inside the mobile layout, so shared pages can pick their header. */
  enabled: boolean;
  config: MobileHeaderConfig;
  setConfig: (config: MobileHeaderConfig) => void;
  /**
   * Whether the current page wants the header + bottom bar to hide on scroll —
   * only feed / post pages opt in. Read by the mobile chrome.
   */
  scrollHide: boolean;
  setScrollHide: (value: boolean) => void;
  /**
   * The active tab panel's scroll container when the page uses SwipeTabViews
   * (per-tab scrolling). Non-null switches the chrome into "panel mode": the
   * layout stops scrolling itself, the header overlays, and hide-on-scroll
   * watches this element instead of the layout scroller.
   */
  panelScrollEl: HTMLElement | null;
  setPanelScrollEl: (el: HTMLElement | null) => void;
  /** The current page's primary action, or null when the bar is navigation. */
  composeAction: MobileComposeAction | null;
  setComposeAction: (action: MobileComposeAction | null) => void;
  /**
   * Immersive mode: the page has taken over the screen and wants no chrome at
   * all — no top bar, no tabs, no bottom bar. Used by an open chat, which is a
   * state inside the Inbox page rather than a route, so route-based fullscreen
   * cannot express it.
   */
  immersive: boolean;
  setImmersive: (value: boolean) => void;
}

const MobileHeaderContext = createContext<MobileHeaderContextValue>({
  enabled: false,
  config: null,
  setConfig: () => undefined,
  scrollHide: false,
  setScrollHide: () => undefined,
  panelScrollEl: null,
  setPanelScrollEl: () => undefined,
  composeAction: null,
  setComposeAction: () => undefined,
  immersive: false,
  setImmersive: () => undefined,
});

/** Wraps the mobile layout so pages can inject header content into the top bar. */
export const MobileHeaderProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [config, setConfig] = useState<MobileHeaderConfig>(null);
  const [scrollHide, setScrollHide] = useState(false);
  const [panelScrollEl, setPanelScrollEl] = useState<HTMLElement | null>(null);
  const [composeAction, setComposeAction] = useState<MobileComposeAction | null>(null);
  const [immersive, setImmersive] = useState(false);
  const value = useMemo(
    () => ({
      enabled: true,
      config,
      setConfig,
      scrollHide,
      setScrollHide,
      panelScrollEl,
      setPanelScrollEl,
      composeAction,
      setComposeAction,
      immersive,
      setImmersive,
    }),
    [config, scrollHide, panelScrollEl, composeAction, immersive]
  );
  return <MobileHeaderContext.Provider value={value}>{children}</MobileHeaderContext.Provider>;
};

/** Read the active header config (used by the top bar to render it). */
export const useMobileHeaderConfig = (): MobileHeaderConfig => useContext(MobileHeaderContext).config;

/**
 * Whether the mobile header slot is available. Shared web/mobile pages use this
 * to decide between injecting into the top bar vs. rendering their own header.
 */
export const useMobileHeaderEnabled = (): boolean => useContext(MobileHeaderContext).enabled;

/**
 * Register the current page's header content (tabs or a heading) into the shared
 * mobile top bar. Pass `deps` like a useEffect dependency list (e.g. the active
 * tab) so the header re-syncs when they change. Passing `null` clears the slot.
 */
export const useMobileHeader = (config: MobileHeaderConfig, deps: React.DependencyList): void => {
  const { setConfig } = useContext(MobileHeaderContext);

  // Re-sync whenever the caller's deps change. No intermediate null is written
  // on updates, so the header never flickers between pages/tab changes.
  useEffect(() => {
    setConfig(config);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  // Clear only when the page unmounts.
  useEffect(
    () => () => setConfig(null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
};

/** Whether the mobile chrome (header + bottom bar) should hide on scroll. */
export const useMobileScrollHideEnabled = (): boolean =>
  useContext(MobileHeaderContext).scrollHide;

/** The active tab panel's scroller (null when the page has no swipeable tabs). */
export const useMobilePanelScrollEl = (): HTMLElement | null =>
  useContext(MobileHeaderContext).panelScrollEl;

/** Used by SwipeTabViews to publish its active panel's scroll container. */
export const useSetMobilePanelScrollEl = (): ((el: HTMLElement | null) => void) =>
  useContext(MobileHeaderContext).setPanelScrollEl;

/**
 * Opt the current page into hide-on-scroll for the mobile header + bottom bar.
 * Feed/post pages call `useMobileScrollHide(true)`; it no-ops on web (no
 * provider) and auto-resets when the page unmounts.
 */
export const useMobileScrollHide = (enabled: boolean): void => {
  const { setScrollHide } = useContext(MobileHeaderContext);
  useEffect(() => {
    setScrollHide(enabled);
    return () => setScrollHide(false);
  }, [enabled, setScrollHide]);
};

/** Read the active compose action (used by the bottom bar to render it). */
export const useMobileComposeActionConfig = (): MobileComposeAction | null =>
  useContext(MobileHeaderContext).composeAction;

/**
 * Hand the bottom bar this page's primary action, turning it from navigation
 * into a single Post/Update button. Same contract as useMobileHeader: pass
 * `deps` so the label and disabled state stay current, and the slot clears on
 * unmount so leaving compose restores navigation.
 */
export const useMobileComposeAction = (
  action: MobileComposeAction | null,
  deps: React.DependencyList
): void => {
  const { setComposeAction } = useContext(MobileHeaderContext);

  useEffect(() => {
    setComposeAction(action);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  useEffect(
    () => () => setComposeAction(null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
};

/** Whether the current page has taken over the screen (read by the chrome). */
export const useMobileImmersiveEnabled = (): boolean =>
  useContext(MobileHeaderContext).immersive;

/**
 * Take over the screen: hides the top bar, its tabs and the bottom bar for as
 * long as `enabled` holds. Clears on unmount so backing out always restores the
 * chrome, even if the page unmounts while still immersive.
 */
export const useMobileImmersive = (enabled: boolean): void => {
  const { setImmersive } = useContext(MobileHeaderContext);

  useEffect(() => {
    setImmersive(enabled);
    return () => setImmersive(false);
  }, [enabled, setImmersive]);
};

export default MobileHeaderContext;
